// ============================================================
// سطوح دسترسی
//
// دستیار هیچ‌وقت چیزی را نشان نمی‌دهد که کاربر در منوی سایت هم
// نمی‌بیند. منبع حقیقت، همان آرایه‌ی rolesAllowed در
// Routes/allRoutes.tsx است — اینجا فقط تکرار شده تا دستیار بتواند
// قبل از زدن به API تصمیم بگیرد. اگر نقشی به مسیری اضافه شد،
// همین‌جا هم باید اضافه شود.
// ============================================================

import { getRoleId, getUsername } from "Common/Components/getRoleId";

export const TOKEN_KEY = "app_havir";

export const ROLE_LABELS: Record<number, string> = {
    0: "سوپر ادمین",
    1: "ادمین",
    2: "بازرس شهرستان",
    3: "ناظر هوش مصنوعی",
    4: "راننده",
    5: "مدیر استان",
    6: "مدیر شهرستان",
    7: "شرکت حمل",
};

export const currentRole = (): number | null => getRoleId(localStorage.getItem(TOKEN_KEY));

export const currentUsername = (): string | null => getUsername(localStorage.getItem(TOKEN_KEY));

export const roleLabel = (role: number | null): string =>
    role === null ? "مهمان" : ROLE_LABELS[role] || "نقش " + role;

export const canAccess = (role: number | null, roles: number[]): boolean =>
    role !== null && roles.indexOf(role) !== -1;

/** صفحات سایت — برای دستور «منو ببر به ...» */
export interface SitePage {
    id: string;
    title: string;
    link: string;
    roles: number[];
    /** الگوهایی که کاربر ممکن است برای این صفحه بگوید */
    tags: string[];
}

export const SITE_PAGES: SitePage[] = [
    {
        id: "orders",
        title: "حواله های نانوا",
        link: "/ManageOrders",
        roles: [0, 1, 3, 5, 6, 7],
        tags: ["حواله های نانوا", "حواله ها", "حواله", "سفارش ها", "سفارشات", "مدیریت حواله"],
    },
    {
        id: "driver-tasks",
        title: "وظایف راننده",
        link: "/DriverTasks",
        roles: [0, 1, 4, 5, 6, 7],
        // «برو به راننده ها» و «صفحه راننده‌ها» هم باید بفهمد
        tags: [
            "وظایف راننده", "تخلیه راننده", "کارهای راننده", "پنل راننده",
            "صفحه راننده", "راننده ها", "رانندها", "راننده",
        ],
    },
    {
        id: "inspector-tasks",
        title: "وظایف بازرس شهرستان",
        link: "/InspectorTasks",
        roles: [0, 1, 2, 5, 6],
        tags: [
            "وظایف بازرس", "بازرس شهرستان", "کارهای بازرس", "بازرسی",
            "صفحه بازرس", "بازرس ها", "بازرسا", "بازرس",
        ],
    },
    {
        id: "ai-tasks",
        title: "وظایف ناظر هوش مصنوعی",
        link: "/AITasks",
        roles: [0, 1, 3, 5, 6],
        tags: ["ناظر هوش مصنوعی", "وظایف هوش مصنوعی", "شمارش خودکار"],
    },
    {
        id: "quota",
        title: "سهمیه خبازی ها",
        link: "/BakerQuota",
        roles: [0, 1, 2, 5, 6],
        // این دو صورت، خطای پرتکرار تشخیص گفتار برای «برو به سهمیه خبازی‌ها» هستند.
        tags: ["سهمیه خبازی", "سهمیه ها", "سهمیه", "سهميه نانوا", "برو بازی ها", "بروبازی ها"],
    },
    {
        id: "violations",
        title: "گزارش تخلفات",
        link: "/ViolationsReport",
        roles: [0, 1, 2, 5, 6],
        tags: ["گزارش تخلفات", "تخلفات", "تخلف", "خارج از شبکه", "کم پزی"],
    },
    {
        id: "users",
        title: "مدیریت کاربران",
        link: "/manageUser",
        roles: [0, 1, 5, 6],
        tags: ["مدیریت کاربران", "کاربران", "کاربرها", "لیست کاربران"],
    },
    {
        id: "reports",
        title: "گزارش ها",
        link: "/reports",
        roles: [0, 1, 3, 5, 6, 7],
        tags: ["گزارش ها", "گزارش توزیع آرد", "گزارش روزانه", "گزارش تاریخ"],
    },
    {
        id: "reports-decade",
        title: "گزارش دهه",
        link: "/ReportsDecadePage",
        roles: [0, 1, 3, 5, 6, 7],
        tags: ["گزارش دهه", "دهه", "گزارش دوره", "مرحله توزیع"],
    },
    {
        id: "bakeries",
        title: "اطلاعات خبازی ها",
        link: "/BakeryInformation",
        roles: [0, 1, 2, 5, 6],
        tags: ["اطلاعات خبازی", "خبازی ها", "خبازی", "نانوایی", "اطلاعات نانوا", "پروفایل خبازی"],
    },
    {
        id: "quota-upload",
        title: "بارگذاری سهمیه خبازی ها",
        link: "/BakeryQuotas/upload",
        roles: [0, 1],
        tags: ["بارگذاری سهمیه", "آپلود سهمیه", "اکسل سهمیه"],
    },
    {
        id: "admin-settings",
        title: "تنظیمات تخلیه و بازرسی",
        link: "/AdminSettings",
        roles: [0],
        tags: ["تنظیمات تخلیه", "تنظیمات بازرسی", "مهلت تخلیه", "تنظیمات ادمین"],
    },
    {
        id: "profile",
        title: "پروفایل کاربر",
        link: "/user-profile",
        roles: [0, 1, 2, 3, 4, 5, 6],
        tags: ["پروفایل", "حساب من", "پروفایل من", "اطلاعات حساب"],
    },
];

export const visiblePages = (role: number | null): SitePage[] =>
    SITE_PAGES.filter((p) => canAccess(role, p.roles));
